import React from "react";
import debug from "debug";
import cns from "classnames";
import Backdrop from "@material-ui/core/Backdrop";
import Popper from "@material-ui/core/Popper";
import { ReactSelectMuiProps } from "./ReactSelectMui";
import { MenuRendererProps, sortOptionsToSortBy } from "./utils";
import { SelectOptionMui as SelectOptionPlain } from "./SelectOptionPlain";
import { SelectOptionMui as SelectOptionCheckbox } from "./SelectOptionCheckbox";

const log = debug("app:Select:GroupedMenuRenderer");

type Props = MenuRendererProps & {
  closeGroup: (group: string) => void;
  hideGroupCheckbox?: boolean;
  onClose: (e: React.SyntheticEvent<any>) => void;
  openedGroups: string[];
  openGroup: (group: string) => void;
  selectProps: ReactSelectMuiProps;
  sortGroups: boolean;
};

const GroupedMenuRenderer: React.FC<Props> = (props) => {
  log("groupedMenuRenderer", props);

  const {
    focusedOption,
    focusOption,
    inputValue,
    instancePrefix,
    onClose,
    onFocus,
    onOptionRef,
    onSelect,
    openedGroups,
    optionClassName: optionClassNameProp,
    options: optionsProp,
    removeValue,
    renderMenu,
    selectProps,
    selectValue,
    sortGroups,
    valueArray,
    valueKey
  } = props;

  const { groupBy, groupByField, hideSameNameChildInGroups, optionType, sortOptions } =
    selectProps;

  const OptionCmp = props.optionComponent;

  let options = sortOptions
    ? optionsProp.sortBy(...sortOptionsToSortBy(sortOptions))
    : optionsProp;

  if (hideSameNameChildInGroups) {
    options = options.filter((x) => x.label !== x.parent);
  }

  // if groupBy is string take elements with no groupBy field as root elements
  let groups = options
    .filter((o) => !o[groupBy])
    .map((o) => ({
      ...o,
      children: options.filter((c) => c[groupBy] === o[groupByField])
    }));
  log("groups filtered", groups);

  // sorting is hardcoded in
  if (sortGroups) {
    groups = groups.sortBy((x) => (typeof x.label === "string" ? x.label : true));
    log("groups sorted", groups);
  }

  // filter empty groups out when user has typed some search text
  if (inputValue) {
    // groups = groups.filter(g => !g.children || (g.children && g.children.length > 0));
    // log("groups with empty groups removed", groups);
  }

  // if (hideSameNameChildInGroups) {
  //   groups = groups.map(g => ({
  //     ...g,
  //     children: Array.isArray(g.children) && g.children.length > 1 ? g.children : []
  //   }));
  // }

  const anchor = document.getElementById(`${instancePrefix}-value`).parentElement
    .parentElement;
  const anchorWidth = anchor.getBoundingClientRect().width;

  let menu = (
    <div className="Select-menu-dropdown" style={{ minWidth: anchorWidth }}>
      {Object.keys(groups).map((key, ind) => {
        const group = groups[key];

        const GroupCmp = props.hideGroupCheckbox
          ? SelectOptionPlain
          : optionType === "checkbox"
          ? SelectOptionCheckbox
          : SelectOptionPlain;

        const isGroupSelected =
          valueArray && valueArray.some((x) => x[valueKey] === group.value);

        const isGroupOpen =
          // if component supports grouping there is a search value already, then
          // we can have all the groups opened, because only those that have elements found
          // will be rendered
          Boolean(groupBy && inputValue) || openedGroups.includes(`group${ind}`);

        const hasChildren = Array.isArray(group.children) && group.children.length >= 1;

        function toggleGroup() {
          const gr = `group${ind}`;
          openedGroups.includes(gr) ? props.closeGroup(gr) : props.openGroup(gr);
        }

        return (
          <div key={group[groupByField]}>
            <GroupCmp
              inputValue={props.inputValue}
              isGroup={hasChildren}
              isSelected={isGroupSelected}
              onFocus={props.onFocus}
              onSelect={props.hideGroupCheckbox ? () => toggleGroup() : props.onSelect}
              option={group}
              parentLevel={0}
              isGroupOpen={isGroupOpen}
              handleDropdownItemClick={(e) => {
                e.stopPropagation();
                toggleGroup();
              }}
            >
              {group.label}
            </GroupCmp>

            {isGroupOpen &&
              hasChildren &&
              group.children.map((option, i) => {
                const isSelected =
                  isGroupSelected ||
                  (valueArray &&
                    valueArray.some((x) => x[valueKey] === option[valueKey]));

                const isFocused = option === focusedOption;

                const optionClassName = cns(optionClassNameProp, {
                  "Select-option": true,
                  "is-selected": isSelected,
                  "is-focused": isFocused,
                  "is-disabled": option.disabled
                });

                const isDisabled = isGroupSelected || option.disabled;

                return (
                  <OptionCmp
                    key={`option-${i}-${option[valueKey]}`}
                    className={optionClassName}
                    focusOption={focusOption}
                    inputValue={inputValue}
                    instancePrefix={instancePrefix}
                    isDisabled={isDisabled}
                    isFocused={isFocused}
                    isSelected={isSelected}
                    onFocus={onFocus}
                    onSelect={onSelect}
                    option={option}
                    optionIndex={i} // probably meaningless in grouped setting
                    parentLevel={1}
                    ref={(ref) => {
                      onOptionRef(ref, isFocused);
                    }}
                    removeValue={removeValue}
                    selectValue={selectValue}
                  >
                    {option.label}
                  </OptionCmp>
                );
              })}
          </div>
        );
      })}
    </div>
  );

  menu = renderMenu ? renderMenu(menu) : menu;

  return (
    <Popper
      open={true}
      id={instancePrefix}
      anchorEl={anchor}
      style={{
        zIndex: 9999
      }}
      placement="bottom-start"
    >
      <Backdrop open invisible onClick={onClose} />
      {menu}
    </Popper>
  );
};

export default GroupedMenuRenderer;
